import React from 'react';
import { APPS_DATA } from '../data/mockData';
import { getGrowthClassification } from './GrowthScoreGauge';
import { AppIcon } from './AppIcon';
import { ArrowLeftRight, Star, ArrowUpRight } from 'lucide-react';

interface CompetitorTableProps {
  competitorIds: string[];
  currentAppId: string;
  onCompare: (appIds: string[]) => void;
  onSelectApp: (appId: string) => void;
}

export const CompetitorTable: React.FC<CompetitorTableProps> = ({
  competitorIds = [],
  currentAppId,
  onCompare,
  onSelectApp
}) => {
  const safeCompetitorIds = Array.isArray(competitorIds) ? competitorIds : [];
  const competitors = safeCompetitorIds
    .map((id) => APPS_DATA[id])
    .filter(Boolean);

  if (competitors.length === 0) return null;

  const handleCompareAll = () => {
    onCompare([currentAppId, ...safeCompetitorIds.slice(0, 3)]);
  };

  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-100">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Market Landscape</span>
          <h3 className="text-lg font-bold text-slate-900 tracking-tight">Competitor Intelligence</h3>
        </div>
        <button
          onClick={handleCompareAll}
          className="inline-flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 px-3.5 py-1.5 rounded-xl text-xs font-bold border border-blue-200 transition-all"
        >
          <ArrowLeftRight className="w-3.5 h-3.5" strokeWidth={2} />
          <span>Compare All Head-to-Head</span>
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-slate-100 text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
              <th className="pb-3 pl-2">Competitor App</th>
              <th className="pb-3 text-center">Rank</th>
              <th className="pb-3 text-center">Growth Score</th>
              <th className="pb-3 text-center">Rating</th>
              <th className="pb-3 text-center">
                <span>Est. Downloads</span>
                <span className="block text-[9px] text-blue-500 font-normal">Model ASV-DL-v1.2</span>
              </th>
              <th className="pb-3 text-right pr-2">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {competitors.map((comp) => {
              const { label, bg } = getGrowthClassification(comp.growthScore);
              return (
                <tr key={comp.id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-3.5 pl-2">
                    <div
                      onClick={() => onSelectApp(comp.id)}
                      className="flex items-center gap-3 cursor-pointer group"
                    >
                      <AppIcon
                        app={comp}
                        size="xs"
                        className="w-9 h-9 rounded-xl shadow-sm group-hover:scale-105 transition-transform flex-shrink-0"
                      />
                      <div>
                        <span className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors block">
                          {comp.name}
                        </span>
                        <span className="text-[11px] text-slate-400">{comp.developer}</span>
                      </div>
                    </div>
                  </td>

                  <td className="py-3.5 text-center">
                    <span className="font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded-md">
                      #{comp.currentRank}
                    </span>
                  </td>

                  <td className="py-3.5 text-center">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${bg}`}>
                      {comp.growthScore} • {label}
                    </span>
                  </td>

                  <td className="py-3.5 text-center font-medium text-slate-800">
                    <span className="inline-flex items-center justify-center gap-1">
                      <Star className="w-3 h-3 fill-amber-400 text-amber-400" strokeWidth={1.5} />
                      <span>{comp.rating.toFixed(1)}</span>
                    </span>
                  </td>

                  <td className="py-3.5 text-center font-medium text-slate-600">
                    {(comp.estimatedMonthlyDownloads / 1000000).toFixed(1)}M / mo
                  </td>

                  <td className="py-3.5 text-right pr-2">
                    <button
                      onClick={() => onCompare([currentAppId, comp.id])}
                      className="inline-flex items-center gap-0.5 text-xs font-bold text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      <span>Compare</span>
                      <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
